import React from 'react';

import { Card } from '../../../../../../components/Card/Card';
import { DonutWithBars, Segment } from '../../../../../../components/DonutWithBars/DonutWithBars';
import { NoData } from '../../../../../../components/NoData/NoData';
import { Tooltip } from '../../../../../../components/Tooltip/Tooltip';
import { graphql } from '../../../../../../generated';
import { t } from '../../../../../../locales/translate';
import { withSuspendedCard } from '../../../../../../util/suspense';
import { Icon } from '../../../../../../components/Icon/Icon';
import { compact } from 'lodash';
import { useSuspenseQuery } from '../../../../../../util/use_suspense_query';

const PRODUCT_GENDER_SALES = graphql(`
  query ProductGenderSales($id: ID!) {
    product(id: $id) {
      id
      genderSalesStats {
        soldToMUsers
        soldToFUsers
        soldToXUsers
      }
    }
  }
`);

const CATEGORY_GENDER_SALES = graphql(`
  query CategoryGenderSales($id: ID!) {
    category(id: $id) {
      id
      genderSalesStats {
        soldToFUsers
        soldToMUsers
        soldToXUsers
      }
    }
  }
`);

const queries: Record<SaleType, QueryType> = {
  product: PRODUCT_GENDER_SALES,
  category: CATEGORY_GENDER_SALES,
};

type SaleType = 'product' | 'category';
type QueryType = typeof PRODUCT_GENDER_SALES | typeof CATEGORY_GENDER_SALES;

interface Props {
  saleId: string;
  saleType: SaleType;
}

const colors = {
  soldToMUsers: '#2d9cdb',
  soldToFUsers: '#eb5757',
  soldToXUsers: '#676767',
};

function SalesByGender$({ saleId, saleType }: Props): JSX.Element {
  const { data } = useSuspenseQuery(queries[saleType], {
    variables: { id: saleId },
  });
  const genderSalesStats = data?.[saleType]?.genderSalesStats;
  const parsedData: Segment[] = genderSalesStats
    ? compact(
        Object.keys(genderSalesStats).map((key) => {
          if (['soldToMUsers', 'soldToFUsers', 'soldToXUsers'].includes(key)) {
            return {
              name: t.general.genders[key],
              value: genderSalesStats?.[key],
              color: colors[key],
            };
          }

          return null;
        })
      )
    : [];

  return (
    <Card
      title={t.page.event.salesByGender}
      tooltip={() => (
        <Tooltip
          reference={<Icon name="help--sm" color="var(--color-text-label)" />}
          position="bottom"
          fontSize="small"
          padding="medium"
          darkMode
          small
        >
          {t.page.event.salesByGenderTooltip}
        </Tooltip>
      )}
    >
      {parsedData.some((data) => data.value > 0) ? (
        <DonutWithBars data={parsedData} maximumFractionDigits={1} />
      ) : (
        <NoData />
      )}
    </Card>
  );
}

export const SalesByGender = withSuspendedCard(SalesByGender$, {
  loadingTitle: t.page.event.salesByGender,
});
